import React, { useEffect }  from 'react';
import { pick } from 'lodash';
import PropTypes from 'prop-types';
import { Form, Input, Button } from 'components';
import { SelectRegion, SelectProvince, SelectMunicipality, SelectBarangay } from 'containers';
import { useForm, useFormValidator } from '../hooks';

const initFormData = {
  name: '',
  sec_cda_registration_number: '',
  tin_number: '',
  email: '',
  contact_number: '',
  street: '',
  mailing_street: '',
}

const rules = {
  name: 'required|text_vapt',
  sec_cda_registration_number: 'required',
  tin_number: 'required|tin_number',
  email: 'required|email',
  street: 'required',
  mailing_street: 'required',
  contact_number: 'required|mobile_number',
}

const initFormDataBusinessAddress = {
  region_id: '',
  province_id: '',
  municipality_id: '',
  barangay_id: '',
}

const businessAddressRules = {
  region_id: 'required',
  province_id: 'required',
  municipality_id: 'required',
  barangay_id: 'required',
}

const initFormDataMailingAddress = {
  mailing_region_id: '',
  mailing_province_id: '',
  mailing_municipality_id: '',
  mailing_barangay_id: '',
}

const mailingAddressRules = {
  mailing_region_id: 'required',
  mailing_province_id: 'required',
  mailing_municipality_id: 'required',
  mailing_barangay_id: 'required',
}

export const options = {
  initialFormData: {
    ...initFormData,
    ...initFormDataBusinessAddress,
    ...initFormDataMailingAddress
  },
  rules: {
    ...rules,
    ...businessAddressRules,
    ...mailingAddressRules
  }
}

const CorpoCoopOtherInformationForm = ({  onNext, initialValue }) => {

  const [value, { onChange, onSetForm }] = useForm(initFormData)
  const { validate, renderMessage, response } = useFormValidator(value, rules)
  
  const [businessAddressFormData, businessAddressFormAction] = useForm(initFormDataBusinessAddress)
  const businessAddressFormValidator = useFormValidator(businessAddressFormData, businessAddressRules)

  const [mailingAddressFormData, mailingAddressFormAction] = useForm(initFormDataMailingAddress)
  const mailingAddressFormValidator = useFormValidator(mailingAddressFormData, mailingAddressRules)
  
  const combinedPayload = {
    ...value,
    ...businessAddressFormData,
    ...mailingAddressFormData
  }

  useEffect(() => {
    onSetForm(pick(initialValue, Object.keys(initFormData)))
    businessAddressFormAction.onSetForm(pick(initialValue, Object.keys(initFormDataBusinessAddress)))
    mailingAddressFormAction.onSetForm(pick(initialValue, Object.keys(initFormDataMailingAddress)))
    // eslint-disable-next-line
  }, [Object.values(initialValue).join(',')])

  useEffect(() => {
    validate()
    // eslint-disable-next-line
  }, [Object.values(value).join(',')])

  useEffect(() => {
    businessAddressFormValidator.validate()
    // eslint-disable-next-line
  }, [Object.values(businessAddressFormData).join(',')])

  useEffect(() => {
    mailingAddressFormValidator.validate()
    // eslint-disable-next-line
  }, [Object.values(mailingAddressFormData).join(',')])
  
  const isValidationPassed = (response.is_passed && businessAddressFormValidator.response.is_passed && mailingAddressFormValidator.response.is_passed)

  return (
    <>
      <Form.FormGroup className="form-group-2">
        <Input label="Name of Corporation/Cooperatives/Other" name="name" onChange={onChange} value={value.name} helperMessage={renderMessage}/>
        <Input label="SEC/CDA Registration Number" name="sec_cda_registration_number" onChange={onChange} value={value.sec_cda_registration_number} helperMessage={renderMessage}/>
      </Form.FormGroup>
      <Form.FormGroup  className="form-group-4">
        <Input type="mask" mask="999-999-999-99999" placeholder='---_---_---_-----' label="TIN Number" name="tin_number" onChange={onChange} value={value.tin_number} helperMessage={renderMessage}/>
        <Input label="Email" name="email" onChange={onChange} value={value.email} helperMessage={renderMessage}/>
        <Input type="mask" mask="99999999999" placeholder='09000000000' label="Contact Number" name="contact_number" onChange={onChange} value={value.contact_number} helperMessage={renderMessage}/>
      </Form.FormGroup>
      <span className="text-success text-sm font-bold">Business Address</span>
      <Form.FormGroup className="form-group-4">
        <SelectRegion 
          onChange={businessAddressFormAction.onChange} 
          value={businessAddressFormData.region_id} 
          helperMessage={businessAddressFormValidator.renderMessage}  
        />
        <SelectProvince 
          region_id={businessAddressFormData.region_id} 
          onChange={businessAddressFormAction.onChange} 
          value={businessAddressFormData.province_id} 
          helperMessage={businessAddressFormValidator.renderMessage}  
        />
        <SelectMunicipality 
          region_id={businessAddressFormData.region_id} 
          province_id={businessAddressFormData.province_id} 
          onChange={businessAddressFormAction.onChange} 
          value={businessAddressFormData.municipality_id} 
          helperMessage={businessAddressFormValidator.renderMessage}  
        />
        <SelectBarangay 
          region_id={businessAddressFormData.region_id} 
          province_id={businessAddressFormData.province_id} 
          municipality_id={businessAddressFormData.municipality_id} 
          onChange={businessAddressFormAction.onChange} 
          value={businessAddressFormData.barangay_id} 
          helperMessage={businessAddressFormValidator.renderMessage}  
        />
      </Form.FormGroup>
      <Form.FormGroup className="form-group">
        <Input label="Street" name="street" onChange={onChange} value={value.street} helperMessage={renderMessage}/>
      </Form.FormGroup>
      <span className="text-success text-sm font-bold">Mailing Address</span>
      <Form.FormGroup className="form-group-4">
        <SelectRegion 
          prefixName="mailing_"
          onChange={mailingAddressFormAction.onChange} 
          value={mailingAddressFormData.mailing_region_id} 
          helperMessage={mailingAddressFormValidator.renderMessage}  
        />
        <SelectProvince 
          prefixName="mailing_"
          region_id={mailingAddressFormData.mailing_region_id} 
          onChange={mailingAddressFormAction.onChange} 
          value={mailingAddressFormData.mailing_province_id} 
          helperMessage={mailingAddressFormValidator.renderMessage}  
        />
        <SelectMunicipality 
          prefixName="mailing_"
          region_id={mailingAddressFormData.mailing_region_id} 
          province_id={mailingAddressFormData.mailing_province_id} 
          onChange={mailingAddressFormAction.onChange} 
          value={mailingAddressFormData.mailing_municipality_id} 
          helperMessage={mailingAddressFormValidator.renderMessage}  
        />
        <SelectBarangay 
          prefixName="mailing_"
          region_id={mailingAddressFormData.mailing_region_id} 
          province_id={mailingAddressFormData.mailing_province_id} 
          municipality_id={mailingAddressFormData.mailing_municipality_id} 
          onChange={mailingAddressFormAction.onChange} 
          value={mailingAddressFormData.mailing_barangay_id} 
          helperMessage={mailingAddressFormValidator.renderMessage}  
        />
      </Form.FormGroup>
      <Form.FormGroup className="form-group">
        <Input label="Street" name="mailing_street" onChange={onChange} value={value.mailing_street} helperMessage={renderMessage}/>
      </Form.FormGroup>
      <div className="flex space-x-2 float-right">
        <Button 
          disabled={!isValidationPassed} 
          type="button" onClick={onNext(combinedPayload)} className="!bg-warning !w-auto !text-black">Next</Button>
      </div>
    </>
  )
}


CorpoCoopOtherInformationForm.propTypes = {
  onNext: PropTypes.instanceOf(Function).isRequired,
  initialValue: PropTypes.instanceOf(Object).isRequired
}

CorpoCoopOtherInformationForm.displayName = 'CorpoCoopOtherInformationForm'


export default CorpoCoopOtherInformationForm